import React, { useCallback, useEffect, useRef, useState } from 'react';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import FeatureCard from 'features/feed/components/FeatureCard';
import useMonevoEntry from 'features/borrow/monevo/hooks/useMonevoEntry';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import Amplitude from 'utils/amplitude';
import Links from 'utils/links';
import { StyleSheet, View } from 'react-native';
import { rem } from 'design-system';
import Text from 'design-system/Text';
import Collapsible from 'utils/packages/rnCollapsible';
import IconLineChart from 'design-system/icons/IconLineChart';
import NotificationBanner from 'design-system/NotificationBanner';
import useCreditReportDebtTrackingFlow from '../hooks/useCreditReportDebtTrackingFlow';
import CreditReportDebtLogos from './CreditReportDebtLogos';
import { selectUntrackedCreditReportDebts } from '../selectors';
import { isWeb } from '../../../constants';
const COLLAPSE_DURATION = 300;
const OfferCardShell = ({ sectionTitle, isCollapsed, children }) => (<Collapsible collapsed={isCollapsed}>
    {!!sectionTitle && (<View style={styles.sectionHeader}>
        <Text Gray Text-16>
          {sectionTitle}
        </Text>
      </View>)}
    {children}
  </Collapsible>);
const strings = {
    creditCardOffers: {
        title: 'Check credit card offers',
        description: 'See cards tailored to your profile. Having a card and using it responsibly can help build your credit',
        cta: 'Check eligibility',
    },
    consolidateDebt: {
        title: 'Consolidate debt',
        description: 'See if you can combine your debt into one loan',
        cta: 'Continue',
    },
    trackDebts: {
        title: 'Track your debts in net worth',
        description: 'Automatically import loan and mortgage balances from your credit report',
        cta: 'Add to net worth',
    },
};
const useDismissOfferCard = (key) => {
    const dispatch = useAppDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const timeoutRef = useRef(undefined);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const onPressCancel = useCallback(() => {
        setIsCollapsed(true);
        timeoutRef.current = setTimeout(() => {
            dispatch(setDeviceSettingsKey(key, Date.now()));
        }, COLLAPSE_DURATION);
    }, [dispatch, key]);
    return { isCollapsed, onPressCancel };
};
export const CreditReportCreditCardOffersCard = ({ source, sectionTitle, style }) => {
    const openMonevo = useMonevoEntry();
    const { isCollapsed, onPressCancel } = useDismissOfferCard('lastTimeDismissCreditReportCreditCardOffers');
    const onPressCta = useCallback(() => {
        Amplitude.logEvent('Credit Report Offer Opened', {
            card: 'creditCardOffers',
            source,
        });
        if (isWeb) {
            Links.openKnownLink('creditCards');
            return;
        }
        openMonevo('CREDIT_CARD');
    }, [openMonevo, source]);
    return (<OfferCardShell sectionTitle={sectionTitle} isCollapsed={isCollapsed}>
        <FeatureCard title={strings.creditCardOffers.title} description={strings.creditCardOffers.description} cta={strings.creditCardOffers.cta} isCollapsed={false} onPressCta={onPressCta} onPressCancel={onPressCancel} style={style}/>
      </OfferCardShell>);
};
export const CreditReportConsolidateDebtCard = ({ source, sectionTitle, style }) => {
    const openMonevo = useMonevoEntry();
    const { isCollapsed, onPressCancel } = useDismissOfferCard('lastTimeDismissCreditReportConsolidateDebt');
    const onPressCta = useCallback(() => {
        Amplitude.logEvent('Credit Report Offer Opened', {
            card: 'consolidateDebt',
            source,
        });
        if (isWeb) {
            Links.openKnownLink('loans');
            return;
        }
        openMonevo('DEBT');
    }, [openMonevo, source]);
    return (<OfferCardShell sectionTitle={sectionTitle} isCollapsed={isCollapsed}>
        <FeatureCard title={strings.consolidateDebt.title} description={strings.consolidateDebt.description} cta={strings.consolidateDebt.cta} isCollapsed={false} onPressCta={onPressCta} onPressCancel={onPressCancel} style={style}/>
      </OfferCardShell>);
};
export const CreditReportTrackDebtsCard = ({ source, style, withMarginTop, withMarginBottom, onTrackingDone }) => {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const { startTracking } = useCreditReportDebtTrackingFlow();
    const untrackedDebts = useAppSelector(selectUntrackedCreditReportDebts);
    // Dismissing remembers which debts were untracked, so the card comes back
    // only when a new debt is imported from a later report.
    const onPressCancel = useCallback(() => {
        const state = store.getState();
        const dismissedIds = untrackedDebts.flatMap(({ networthAccountId }) => networthAccountId != null ? [networthAccountId] : []);
        const previousIds = state.deviceSettings.dismissedTrackCreditReportDebtsIds ?? [];
        dispatch(setDeviceSettingsKey('dismissedTrackCreditReportDebtsIds', [
            ...new Set([...previousIds, ...dismissedIds]),
        ]));
    }, [untrackedDebts]);
    const onPressCta = useCallback(() => {
        startTracking(untrackedDebts, onTrackingDone);
    }, [onTrackingDone, startTracking, untrackedDebts]);
    return (<NotificationBanner noMarginTop={!withMarginTop} noMarginBottom={!withMarginBottom} icon={source === 'accountDetail' ? <IconLineChart /> : undefined} headerContent={source === 'netWorth' ? (<CreditReportDebtLogos debts={untrackedDebts}/>) : undefined} lightButton cancelButtonTitle="Dismiss" positiveButtonTitle={strings.trackDebts.cta} onPositiveBtnPress={onPressCta} onCancel={onPressCancel} title={strings.trackDebts.title} description={strings.trackDebts.description} containerStyles={style}/>);
};
const styles = StyleSheet.create({
    // Mirrors styleSet.sectionHeader in CreditReportAccountSectionsList.
    sectionHeader: {
        marginTop: rem(24),
        marginHorizontal: rem(16),
        paddingBottom: rem(16),
    },
    card: {
        marginBottom: 0,
    },
    cardBelowAccounts: {
        marginBottom: 0,
        marginTop: rem(16),
    },
});
export const CreditReportOfferCard = ({ cardKey, sectionTitle }) => {
    const style = sectionTitle ? styles.card : styles.cardBelowAccounts;
    switch (cardKey) {
        case 'creditCardOffers':
            return (<CreditReportCreditCardOffersCard source="accountsList" sectionTitle={sectionTitle} style={style}/>);
        case 'consolidateDebt':
            return (<CreditReportConsolidateDebtCard source="accountsList" sectionTitle={sectionTitle} style={style}/>);
        case 'trackDebts':
            return (<CreditReportTrackDebtsCard source="accountsList" withMarginTop={!sectionTitle}/>);
        default:
            return null;
    }
};
